import React, { FC, useState, useCallback } from "react";

import { UpdateAppInput, useUpdateApplicationMutation, useGetAppInfoQuery } from "generated/graphql";
import { wrapUrqlMutation } from "utils/graphql";

import { SendLeadsStep, SendLeadsStepProps } from "../../components/setup/SendLeadsStep";
import { ClientApp } from "models/App";

export interface LeadDestinationStepContainerProps extends Omit<SendLeadsStepProps, "onNext" | "app"> {
    app?: Pick<ClientApp, "appId">;
    onNext(): void;
    onBack?(): void;
}

export const LeadDestinationStepContainer: FC<LeadDestinationStepContainerProps> = (props) => {
    const { app, onNext, onBack, ...restProps } = props;

    const [saving, setSaving] = useState(false);

    // Setup updateApplication mutation
    const updateApplication = wrapUrqlMutation(useUpdateApplicationMutation(), {
        onSuccessMessage: false,
    });

    // Get app data to pre-populate lead destination settings
    const [getAppInfo] = useGetAppInfoQuery({
        pause: !app?.appId,
        requestPolicy: "cache-and-network",
        variables: {
            appId: app?.appId || "",
        },
    });

    // Create initialData from current app data (no state needed)
    const appData = getAppInfo.data?.app;

    const handleNext = useCallback(
        async (app: Pick<ClientApp, "appId" | "leadsContact" | "beta">) => {
            setSaving(true);

            try {
                // Transform lead destination data for backend
                const updateAppInput: UpdateAppInput = {
                    leadsContact: app.leadsContact,
                    beta: {
                        ...app?.beta,
                        onboarding: {
                            ...app?.beta?.onboarding,
                            leadsIntegration: app?.beta?.onboarding?.leadsIntegration,
                        },
                    },
                };

                await updateApplication({
                    appId: app?.appId || "",
                    app: updateAppInput,
                });

                // Success - proceed to next step (or finish setup)
                onNext();
            } catch (err) {
                console.error("Failed to update lead destination:", err);
                // Still proceed to next step even if save fails - we don't want to block the user
                onNext();
            } finally {
                setSaving(false);
            }
        },
        [onNext, updateApplication]
    );

    return (
        <SendLeadsStep
            {...restProps}
            app={appData}
            onNext={handleNext}
            onBack={onBack}
            saving={saving}
            loading={!app?.appId || getAppInfo.fetching || getAppInfo.stale}
        />
    );
};
